import React from 'react';
import { App, Typography, Button, Flex, theme } from 'antd';
import { getPlanById } from '@prerender/billing-shared';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { useCurrentPlan } from '../../../hooks/usePrerenderUser';
import { prerenderUserPatch } from '../../../actions/prerenderUserActions';
import { UserSnap } from '~features/CustomerSupport';
import { useGetScheduledChangeQuery, useSchedulePlanCancellationMutation } from '../api/billingApiSlice';
import { useEvent } from '~features/events/hooks/useEvent';
import { useTrial } from '~features/FreeTrial/useTrial';

function CancelPlan() {
  const { message } = App.useApp();
  const { token } = theme.useToken();
  const currentPlan = useCurrentPlan();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isInTrial } = useTrial();
  const { openChurnQuestionnaire } = UserSnap.useUserSnap();
  const scheduledChangeResult = useGetScheduledChangeQuery();
  const [scheduleCancellation, { isLoading: isSchedulingCancellation }] = useSchedulePlanCancellationMutation();
  const { track } = useEvent();
  const scheduledPlanName = scheduledChangeResult.data
    ? getPlanById(scheduledChangeResult.data.planId).name
    : undefined;

  const onCancelPlan = async () => {
    if (isSchedulingCancellation) return;

    track('Billing: Cancel Confirmation Clicked', {
      subscription_plan: currentPlan.id,
    });

    const { data } = await scheduleCancellation({
      replaceScheduledActionConfirmed: Boolean(scheduledChangeResult.data),
    }).unwrap();
    dispatch(prerenderUserPatch({ cancelledAt: new Date(data.cancelledAt * 1000) }));

    message.success('Plan cancellation scheduled');
    openChurnQuestionnaire();
    navigate('/billing/overview');
  };

  return (
    <div style={{ maxWidth: token.screenMD }}>
      <Typography.Title level={4}>Current plan cancellation</Typography.Title>
      <Typography.Paragraph>
        You are currently on {isInTrial ? 'Free Trial of the' : 'the'} {currentPlan.name} plan. After cancellation,
        access will continue until the end of the current billing period.
      </Typography.Paragraph>
      <Typography.Paragraph>
        If you exceed your render limit during this period, extra renders will be billed according to your plan.
      </Typography.Paragraph>
      {scheduledPlanName && (
        <Typography.Paragraph>
          You already have a scheduled plan change to {scheduledPlanName}. Confirming cancellation will remove that plan
          change and schedule cancellation at the end of the current billing period.
        </Typography.Paragraph>
      )}
      <Typography.Paragraph strong>Any pending invoices will still be billed.</Typography.Paragraph>

      <Flex justify="space-between" align="center" style={{ marginTop: token.marginLG }}>
        <Link to="/billing/plans">
          <Button>Back to plans list</Button>
        </Link>
        <Button danger onClick={onCancelPlan} loading={isSchedulingCancellation}>
          Cancel plan
        </Button>
      </Flex>
    </div>
  );
}

export default CancelPlan;
